import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditFleet from "./useEditFleet";
import FleetInformationTab from "./components/fleet-information-tab";
import FleetDocumentTab from "./components/fleet-document-tab";
import FleetComplianceTab from "./components/fleet-compliance-tab";
import { Loader } from "components/spinner/loader";
import { fleetPageRouteName, editFleetPageRouteName, detailFleetPageRouteName } from "../constants/routes";

const EditFleetPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);

  const {
    t,
    form,
    handleSave,
    handleSaveDraft,
    isSubmitting,
    isDraft,
    vehicleTypeOptions,
    vehicleModelOptions,
    freezerModelOptions,
    vendorOptions,
    driverOptions,
    onSearchDrivers,
    onSearchVendors,
    isUploadingPicture,
    handleUploadFleetPicture,
    isUploadingDoc,
    handleUploadDocument,
    expiryWarnings,
    documentFields,
    handleAddDocument,
    handleRemoveDocument,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleConfirmSave,
    isLoading,
  } = useEditFleet(id!);

  const tabs = [
    { label: t("tab-fleet-information"), icon: "truck" as const },
    { label: t("tab-fleet-documents"), icon: "document-text" as const },
    { label: t("tab-fleet-compliance"), icon: "wrench" as const },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-112px)] -mb-3">
      {/* Tabs Header */}
      <div className="flex border-b border-border bg-white -mx-3 px-3">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(index)}
            data-testid={
              index === 0 ? "tab-fleet-information" :
              index === 1 ? "tab-fleet-documents" :
              "tab-fleet-compliance"
            }
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold transition-all border-b-2 -mb-[2px] ${
              activeTab === index
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon name={tab.icon} size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 py-4">
        <section className="rounded-2xl bg-whiteScale-90 px-6 py-6">
          {activeTab === 0 && (
            <FleetInformationTab
              form={form}
              t={t}
              vendorOptions={vendorOptions}
              driverOptions={driverOptions}
              onSearchDrivers={onSearchDrivers}
              onSearchVendors={onSearchVendors}
              isUploadingPicture={isUploadingPicture}
              onUploadPicture={handleUploadFleetPicture}
              vehicleTypeOptions={vehicleTypeOptions}
              vehicleModelOptions={vehicleModelOptions}
              freezerModelOptions={freezerModelOptions}
            />
          )}
          {activeTab === 1 && (
            <FleetDocumentTab
              form={form}
              t={t}
              fields={documentFields}
              onAdd={handleAddDocument}
              onRemove={handleRemoveDocument}
              isUploadingDoc={isUploadingDoc}
              onUpload={handleUploadDocument}
              expiryWarnings={expiryWarnings}
            />
          )}
          {activeTab === 2 && (
            <FleetComplianceTab
              form={form}
              t={t}
            />
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="mt-auto -mx-3 bg-white border-t border-border px-6 py-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-fleet"
        >
          {t("btn-cancel")}
        </Button>
        {isDraft && (
          <Button
            type="button"
            variant="outline"
            color="primary"
            onClick={handleSaveDraft}
            disabled={isSubmitting}
          >
            {t("btn-save-draft" as any)}
          </Button>
        )}
        <Button
          type="button"
          variant="fill"
          color="primary"
          onClick={async () => {
            const isValid = await form.trigger();
            if (!isValid) {
              return;
            }
            setShowSaveModal(true);
          }}
          disabled={isSubmitting}
          data-testid="btn-save-fleet"
        >
          {isSubmitting ? <Icon name="refresh" size={18} className="animate-spin" /> : t("btn-save")}
        </Button>
      </div>

      {/* Modals */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("btn-cancel")}
        onConfirm={async () => {
          setShowSaveModal(false);
          await handleConfirmSave();
        }}
        onCancel={() => setShowSaveModal(false)}
        icon="info"
        loading={isSubmitting}
      />

      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={() => {
          setShowCancelModal(false);
          setTimeout(() => {
            if (location.state?.from === "detail") {
              navigate(detailFleetPageRouteName(id!));
            } else if (location.state?.from === "draft") {
              navigate("/master-data/fleet/draft");
            } else {
              navigate(fleetPageRouteName);
            }
          }, 0);
        }}
        onCancel={() => setShowCancelModal(false)}
      />
    </div>
  );
};

export default EditFleetPage;
